import { useSyncExternalStore } from 'react';

import { analytics } from '@/shared/services/analytics.service';
import type {
  ExperimentKey,
  FlagKey,
  FlagPayload,
  FlagVariant,
} from '@/shared/services/analytics.types';

/**
 * Re-renders the caller whenever PostHog delivers a new flag set. No React
 * context is involved, so these hooks work in Storybook and outside the app
 * tree: without a client they simply report "ready" and return fallbacks.
 */
const useFlagsVersion = (): number =>
  useSyncExternalStore(
    analytics.subscribeFlags,
    analytics.getFlagsVersion,
    analytics.getFlagsVersion,
  );

/**
 * A flag's value, or `fallback` until flags are loaded (first visit) or when
 * the flag is unknown. Returning visitors get the cached value at init, so the
 * fallback is only visible for one round trip on a first visit.
 */
export function useFlag<K extends FlagKey>(
  key: K,
  fallback: FlagVariant<K>,
): FlagVariant<K> {
  useFlagsVersion();
  if (!analytics.flagsReady()) return fallback;
  return analytics.getFlag(key) ?? fallback;
}

/** A flag's JSON payload, `undefined` until loaded or when there is none. */
export function useFlagPayload<K extends FlagKey>(
  key: K,
): FlagPayload<K> | undefined {
  useFlagsVersion();
  if (!analytics.flagsReady()) return undefined;
  return analytics.getFlagPayload(key);
}

export interface ExperimentState<K extends ExperimentKey> {
  /** `'control'` while loading, when the experiment is off or the visitor is excluded. */
  variant: Extract<FlagVariant<K>, string> | 'control';
  payload: FlagPayload<K> | undefined;
  /** First-visit only: flags have not been fetched yet. Render the control or a skeleton. */
  isLoading: boolean;
}

/**
 * The visitor's variant for an experiment. Reading it through `getFlag` emits
 * PostHog's `$feature_flag_called` exposure event, which is what the
 * experiment's results are computed from.
 */
export function useExperiment<K extends ExperimentKey>(
  key: K,
): ExperimentState<K> {
  useFlagsVersion();
  const isLoading = !analytics.flagsReady();
  const raw = isLoading ? undefined : analytics.getFlag(key);
  const variant = (typeof raw === 'string' ? raw : 'control') as
    | Extract<FlagVariant<K>, string>
    | 'control';
  return {
    variant,
    payload: isLoading ? undefined : analytics.getFlagPayload(key),
    isLoading,
  };
}

/**
 * Copy experiments without hardcoded strings: the variant payload names an
 * i18n key (`{ "title_key": "login_title_b" }`) and this returns that key when
 * it is one of `allowed`, otherwise `defaultKey`. Both keys exist in the three
 * language files, so rule #7 holds and PostHog can never inject copy.
 */
export function useExperimentCopyKey<
  K extends ExperimentKey,
  D extends string,
>(key: K, payloadField: string, defaultKey: D, allowed: readonly D[]): D {
  const { payload } = useExperiment(key);
  const candidate =
    payload && typeof payload === 'object' && !Array.isArray(payload)
      ? (payload as Record<string, unknown>)[payloadField]
      : undefined;
  return typeof candidate === 'string' && (allowed as readonly string[]).includes(candidate)
    ? (candidate as D)
    : defaultKey;
}
